import { ModelTier } from "@qw/graphql";
import { PlaybookTypeSchema } from "@qw/qw-common";
import { FileUploadDataSchema } from "@qw/ui";
import { z } from "zod";

// ToolFilter type: null | { whitelist: string[] } | { blacklist: string[] }
const ToolFilterSchema = z
  .union([z.null(), z.object({ whitelist: z.array(z.string()) }), z.object({ blacklist: z.array(z.string()) })])
  .nullable()
  .optional();

/**
 * The form's stand-in for `modelTier: null` — "declare no tier, follow the runtime
 * default". A Radix select item's value can't be empty, so "default" needs a value the
 * form can actually hold, and the editor autosaves every field on every edit, so it has
 * to be a value that gets sent: an absent `modelTier` would leave a declared tier
 * untouched instead of clearing it. Translated back to `null` in `playbook-editor.tsx`,
 * which TypeScript enforces because this is not a `ModelTier`.
 */
export const MODEL_TIER_RUNTIME_DEFAULT = "RUNTIME_DEFAULT";

export const DESCRIPTION_MAX_LENGTH = 512;

export const PlaybookFormSchema = z.object({
  name: z.string().min(1, "Playbook name is required"),
  description: z
    .string()
    .max(DESCRIPTION_MAX_LENGTH, `Description must be ${DESCRIPTION_MAX_LENGTH} characters or less`)
    .optional(),
  parametersDescription: z.string().optional().nullable(),
  workflow: z.string().min(1),
  type: PlaybookTypeSchema,
  toolFilter: ToolFilterSchema,
  modelTier: z
    .union([z.nativeEnum(ModelTier), z.literal(MODEL_TIER_RUNTIME_DEFAULT)])
    .default(MODEL_TIER_RUNTIME_DEFAULT),
  files: z.array(FileUploadDataSchema),
  /**
   * Declared params for a Template playbook, mirrored into the form so that
   * param-only edits (add / remove / rename a field) mark the form dirty and are
   * picked up by autosave and leave protection. The param declarer UI remains the
   * editing surface; this field is what makes those edits persist. Unused by
   * non-Template types, where it stays an empty array.
   */
  templateParamSchema: z.array(z.unknown()).default([]),
});

export type PlaybookFormValues = z.infer<typeof PlaybookFormSchema>;
